/*
 * Rx lens selector
 * ----------------
 * Step-by-step lens flow for frame products: prescription type, Rx, SpectraGuard technology & tint, lens style, lens
 * design, index, coatings and options. The finished job is added to the cart as:
 *   - the frame line, carrying the full Rx as JSON in the hidden `_rx` property plus readable summary properties;
 *   - one line per priced choice (tint, lens, coatings, options), linked to the frame by the shared `_rx_group` id.
 * assets/rx-cart.js keeps those linked lines in sync with the frame line in the cart.
 */
(() => {
  if (window.customElements.get('rx-lens-selector')) {
    return;
  }

  const PRESCRIPTION_TYPES = [
    { key: 'Single Vision', label: 'Single Vision' },
    { key: 'Progressive', label: 'Progressive', description: 'Multifocal' }
  ];

  // Lens styles available for each SpectraGuard technology. The key matches the lens product fields of the
  // rx_lens_design metaobject (clear_product, house_photochromic_product, transitions_gens_product).
  const LENS_STYLES = {
    Sun: [{ key: 'clear', label: 'Clear' }],
    Photochromic: [
      { key: 'house_photochromic', label: 'House Photochromic' },
      { key: 'transitions_gens', label: 'Transitions GenS' }
    ]
  };

  const EYES = [
    { key: 'od', label: 'OD', title: 'Right' },
    { key: 'os', label: 'OS', title: 'Left' }
  ];

  const ADD_ON_GROUPS = [
    { key: 'Coatings', title: 'Coatings' },
    { key: 'Options', title: 'Options' }
  ];

  // ---------------------------------------------------------------------------------------------------------------------
  // Helpers
  // ---------------------------------------------------------------------------------------------------------------------

  const escapeHtml = (value) => String(value ?? '').replace(/[&<>"']/g, (char) => ({
    '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;'
  }[char]));

  const byPosition = (a, b) => (a.position ?? 999) - (b.position ?? 999);

  const formatMoney = (cents, format) => {
    const value = (Number(cents) || 0) / 100;
    const withDelimiters = (amount, decimals, thousands, decimal) => {
      const [whole, fraction] = amount.toFixed(decimals).split('.');
      return whole.replace(/\B(?=(\d{3})+(?!\d))/g, thousands) + (fraction ? decimal + fraction : '');
    };
    return (format || '${{amount}}').replace(/\{\{\s*(\w+)\s*\}\}/, (match, key) => {
      switch (key) {
        case 'amount_no_decimals': return withDelimiters(value, 0, ',', '.');
        case 'amount_with_comma_separator': return withDelimiters(value, 2, '.', ',');
        case 'amount_no_decimals_with_comma_separator': return withDelimiters(value, 0, '.', ',');
        case 'amount_with_apostrophe_separator': return withDelimiters(value, 2, "'", '.');
        default: return withDelimiters(value, 2, ',', '.');
      }
    });
  };

  const uuid = () => (window.crypto?.randomUUID ? window.crypto.randomUUID() : `${Date.now()}-${Math.random().toString(16).slice(2)}`);

  // Ranges are configured in the block settings as "min,max,step", e.g. "-20,12,0.25".
  const parseRange = (text) => {
    const [min, max, step] = String(text || '').split(',').map((part) => part.trim());
    const decimals = (step && step.includes('.')) ? step.split('.')[1].length : 0;
    return { min: Number(min), max: Number(max), step: Number(step) || 1, decimals };
  };

  const rangeValues = (range) => {
    const values = [];
    if (!Number.isFinite(range.min) || !Number.isFinite(range.max) || range.step <= 0) {
      return values;
    }
    const factor = 10 ** range.decimals;
    const steps = Math.round((range.max - range.min) / range.step);
    for (let i = 0; i <= steps; i++) {
      values.push(Math.round((range.min + i * range.step) * factor) / factor);
    }
    return values;
  };

  const formatSigned = (value, decimals) => (value > 0 ? '+' : '') + value.toFixed(decimals);

  // Field definitions for the Rx form. `signed` fields (diopters) are grouped into plano / minus / plus.
  const RX_FIELDS = {
    sph: { label: 'SPH', range: 'sph', signed: true },
    cyl: { label: 'CYL', range: 'cyl', signed: true },
    axis: { label: 'AXIS', range: 'axis', format: (value) => String(value).padStart(3, '0') },
    add: { label: 'ADD', range: 'add', signed: true },
    prism_h: { label: 'Horizontal', range: 'prism' },
    prism_h_base: { label: 'Base', choices: ['IN', 'OUT'] },
    prism_v: { label: 'Vertical', range: 'prism' },
    prism_v_base: { label: 'Base', choices: ['UP', 'DOWN'] },
    base_curve: { label: 'Base curve', range: 'base_curve' },
    seg_height: { label: 'Seg height', range: 'seg_height' },
    oc_height: { label: 'OC height', range: 'oc_height' },
    center_thickness: { label: 'Center thick.', range: 'center_thickness' },
    edge_thickness: { label: 'Edge thick.', range: 'edge_thickness' }
  };

  // ---------------------------------------------------------------------------------------------------------------------
  // Element
  // ---------------------------------------------------------------------------------------------------------------------

  class RxLensSelector extends HTMLElement {
    connectedCallback() {
      if (this._initialized) {
        return;
      }
      this._initialized = true;

      this.config = JSON.parse(this.querySelector('[data-rx-config]').textContent);
      this.config.designs.sort(byPosition);
      this.config.tints.sort(byPosition);
      this.config.addOns.sort(byPosition);
      this.ranges = Object.fromEntries(Object.entries(this.config.ranges).map(([key, text]) => [key, parseRange(text)]));

      this.querySelector('button').addEventListener('click', () => this.open());
      this.dialog = this._createDialog();
    }

    // -------------------------------------------------------------------------------------------------------------------
    // Dialog shell
    // -------------------------------------------------------------------------------------------------------------------

    _createDialog() {
      const dialog = document.createElement('dialog');
      dialog.className = 'rx-dialog';
      dialog.setAttribute('aria-label', 'Select lenses');
      dialog.innerHTML = `
        <div class="rx-dialog__panel">
          <header class="rx-dialog__header">
            <button type="button" class="rx-icon-button" data-rx-back aria-label="Back">&larr;</button>
            <div class="rx-progress" aria-hidden="true"><span class="rx-progress__bar"></span></div>
            <span class="rx-progress__count" data-rx-count></span>
            <button type="button" class="rx-icon-button" data-rx-close aria-label="Close">&times;</button>
          </header>
          <div class="rx-dialog__frame">
            ${this.config.frame.image ? `<img src="${escapeHtml(this.config.frame.image)}" alt="" width="200" height="120" loading="lazy">` : ''}
            <p class="rx-dialog__frame-title">${escapeHtml(this.config.frame.title)}</p>
          </div>
          <div class="rx-dialog__body" data-rx-body></div>
          <footer class="rx-dialog__footer">
            <dl class="rx-summary" data-rx-summary></dl>
            <p class="rx-error" data-rx-error role="alert" hidden></p>
            <button type="button" class="rx-primary" data-rx-next></button>
          </footer>
        </div>
        <div class="rx-info" data-rx-info hidden>
          <div class="rx-info__panel" role="dialog" aria-modal="true">
            <button type="button" class="rx-icon-button rx-info__close" data-rx-info-close aria-label="Close">&times;</button>
            <div data-rx-info-content></div>
          </div>
        </div>
      `;
      document.body.appendChild(dialog);

      dialog.querySelector('[data-rx-close]').addEventListener('click', () => dialog.close());
      dialog.querySelector('[data-rx-back]').addEventListener('click', () => this.back());
      dialog.querySelector('[data-rx-next]').addEventListener('click', () => this.next());
      dialog.querySelector('[data-rx-info-close]').addEventListener('click', () => this._closeInfo());
      dialog.querySelector('[data-rx-info]').addEventListener('click', (event) => {
        if (event.target.matches('[data-rx-info]')) {
          this._closeInfo();
        }
      });
      dialog.addEventListener('click', (event) => {
        if (event.target === dialog) {
          dialog.close();
        }
      });
      dialog.addEventListener('cancel', (event) => {
        if (!this.dialog.querySelector('[data-rx-info]').hidden) {
          event.preventDefault();
          this._closeInfo();
        }
      });
      dialog.querySelector('[data-rx-body]').addEventListener('click', (event) => this._onBodyClick(event));
      dialog.querySelector('[data-rx-body]').addEventListener('change', (event) => this._onBodyChange(event));
      dialog.querySelector('[data-rx-body]').addEventListener('input', (event) => this._onBodyChange(event));
      return dialog;
    }

    open() {
      this.state = {
        type: null,
        patient: '',
        notes: '',
        rx: { od: {}, os: {} },
        pdMode: 'single',
        pd: { single: '', od: '', os: '' },
        technology: null,
        tint: null,
        style: null,
        design: null,
        variantId: null,
        addOns: new Set()
      };
      this.stepIndex = 0;
      this.render();
      this.dialog.showModal();
    }

    // -------------------------------------------------------------------------------------------------------------------
    // Steps
    // -------------------------------------------------------------------------------------------------------------------

    get steps() {
      const steps = ['type', 'rx', 'tint'];
      if (this._stylesForTechnology().length > 1) {
        steps.push('style');
      }
      steps.push('design');
      const product = this._selectedLensProduct();
      if (!product || product.variants.length > 1) {
        steps.push('index');
      }
      ADD_ON_GROUPS.forEach((group) => {
        if (this._addOnsInGroup(group.key).length) {
          steps.push(`addons:${group.key}`);
        }
      });
      steps.push('review');
      return steps;
    }

    get step() {
      return this.steps[this.stepIndex];
    }

    back() {
      if (this.stepIndex === 0) {
        this.dialog.close();
        return;
      }
      this.stepIndex -= 1;
      this.render();
    }

    next() {
      const error = this._validate(this.step);
      if (error) {
        this._showError(error);
        return;
      }
      if (this.step === 'review') {
        this.addToCart();
        return;
      }
      this.stepIndex += 1;
      this.render();
    }

    _validate(step) {
      const { state } = this;
      switch (step) {
        case 'type':
          return state.type ? null : 'Please choose a prescription type.';
        case 'rx':
          return this._validateRx();
        case 'tint':
          return state.tint ? null : 'Please choose a SpectraGuard technology and tint.';
        case 'style':
          return state.style ? null : 'Please choose a lens style.';
        case 'design':
          return state.design ? null : 'Please choose a lens design.';
        case 'index':
          return state.variantId ? null : 'Please choose a lens index.';
        default:
          return null;
      }
    }

    _validateRx() {
      const { state } = this;
      const isProgressive = state.type === 'Progressive';
      const missing = [];

      if (!state.patient.trim()) {
        missing.push('patient name');
      }
      EYES.forEach((eye) => {
        const values = state.rx[eye.key];
        if (values.sph === undefined || values.sph === '') {
          missing.push(`${eye.label} SPH`);
        }
        const hasCyl = values.cyl !== undefined && values.cyl !== '' && Number(values.cyl) !== 0;
        const hasAxis = values.axis !== undefined && values.axis !== '';
        if (hasCyl !== hasAxis) {
          missing.push(`${eye.label} ${hasCyl ? 'AXIS' : 'CYL'} (CYL and AXIS go together)`);
        }
        if (isProgressive && !values.add) {
          missing.push(`${eye.label} ADD`);
        }
        if (isProgressive && !values.seg_height) {
          missing.push(`${eye.label} seg height`);
        }
        ['h', 'v'].forEach((axis) => {
          if (Boolean(values[`prism_${axis}`]) !== Boolean(values[`prism_${axis}_base`])) {
            missing.push(`${eye.label} prism ${axis === 'h' ? 'horizontal' : 'vertical'} amount and base`);
          }
        });
      });
      if (state.pdMode === 'single' && !state.pd.single) {
        missing.push('PD');
      }
      if (state.pdMode === 'dual' && (!state.pd.od || !state.pd.os)) {
        missing.push('PD for both eyes');
      }
      return missing.length ? `Please complete: ${missing.join(', ')}.` : null;
    }

    // -------------------------------------------------------------------------------------------------------------------
    // Data lookups
    // -------------------------------------------------------------------------------------------------------------------

    _stylesForTechnology() {
      return LENS_STYLES[this.state.technology] || [];
    }

    _selectedTint() {
      return this.config.tints.find((tint) => tint.handle === this.state.tint) || null;
    }

    _selectedDesign() {
      return this.config.designs.find((design) => design.handle === this.state.design) || null;
    }

    _selectedLensProduct() {
      const design = this._selectedDesign();
      return design && this.state.style ? design.products[this.state.style] : null;
    }

    _selectedVariant() {
      const product = this._selectedLensProduct();
      return product ? product.variants.find((variant) => variant.id === this.state.variantId) || null : null;
    }

    _availableDesigns() {
      return this.config.designs.filter((design) => (
        (design.types || []).includes(this.state.type) && design.products[this.state.style]
      ));
    }

    _addOnsInGroup(group) {
      return this.config.addOns.filter((addOn) => addOn.group === group && addOn.variantId);
    }

    _selectedAddOns() {
      return this.config.addOns.filter((addOn) => this.state.addOns.has(addOn.handle));
    }

    _lowestPrice(product) {
      return Math.min(...product.variants.map((variant) => variant.price));
    }

    _money(cents) {
      return formatMoney(cents, this.config.moneyFormat);
    }

    // -------------------------------------------------------------------------------------------------------------------
    // Rendering
    // -------------------------------------------------------------------------------------------------------------------

    render() {
      const steps = this.steps;
      const body = this.dialog.querySelector('[data-rx-body]');
      const nextButton = this.dialog.querySelector('[data-rx-next]');

      this.dialog.querySelector('.rx-progress__bar').style.width = `${((this.stepIndex + 1) / steps.length) * 100}%`;
      this.dialog.querySelector('[data-rx-count]').textContent = `${this.stepIndex + 1}/${steps.length}`;
      this._showError(null);

      const [stepName, group] = this.step.split(':');
      body.innerHTML = this[`_render_${stepName}`](group);
      body.scrollTop = 0;

      if (this.step === 'review') {
        nextButton.textContent = 'Add to cart';
      } else if (stepName === 'addons') {
        nextButton.textContent = this._addOnsInGroup(group).some((addOn) => this.state.addOns.has(addOn.handle)) ? 'Continue' : 'Skip';
      } else {
        nextButton.textContent = this.step === 'rx' ? 'Save & continue' : 'Continue';
      }
      this._renderSummary();
    }

    _renderSummary() {
      const tint = this._selectedTint();
      const variant = this._selectedVariant();
      const addOns = this._selectedAddOns();
      const rows = [];

      if (variant) {
        rows.push(['Lens', variant.price]);
      }
      if (tint) {
        rows.push(['SpectraGuard', tint.price]);
      }
      if (addOns.length) {
        rows.push(['Add-ons', addOns.reduce((sum, addOn) => sum + addOn.price, 0)]);
      }
      const total = rows.reduce((sum, [, price]) => sum + price, 0);
      this.dialog.querySelector('[data-rx-summary]').innerHTML = rows.map(([label, price]) => `
        <div><dt>${label}</dt><dd>${this._money(price)}</dd></div>
      `).join('') + `<div class="rx-summary__total"><dt>Subtotal</dt><dd>${this._money(total)}</dd></div>`;
    }

    _optionCard({ name, value, selected, title, description, price, swatch, info, disabled }) {
      return `
        <div class="rx-option${selected ? ' is-selected' : ''}${disabled ? ' is-disabled' : ''}">
          <button type="button" class="rx-option__select" data-rx-${name}="${escapeHtml(value)}" aria-pressed="${selected}" ${disabled ? 'disabled' : ''}>
            ${swatch ? `<span class="rx-swatch" style="--rx-swatch: ${escapeHtml(swatch)}"></span>` : ''}
            <span class="rx-option__text">
              <span class="rx-option__title">${escapeHtml(title)}</span>
              ${description ? `<span class="rx-option__description">${escapeHtml(description)}</span>` : ''}
            </span>
            ${price ? `<span class="rx-option__price">${price}</span>` : ''}
          </button>
          ${info ? `<button type="button" class="rx-option__info" data-rx-info-open="${escapeHtml(value)}" aria-label="More about ${escapeHtml(title)}">?</button>` : ''}
        </div>
      `;
    }

    _render_type() {
      return `
        <h2 class="rx-step-title">Prescription Type</h2>
        <div class="rx-options">
          ${PRESCRIPTION_TYPES.map((type) => this._optionCard({
            name: 'type', value: type.key, selected: this.state.type === type.key, title: type.label, description: type.description
          })).join('')}
        </div>
      `;
    }

    _select(name, field, value, attributes = '') {
      const definition = RX_FIELDS[field];
      let options = '<option value="">&ndash;</option>';

      if (definition.choices) {
        options += definition.choices.map((choice) => `<option value="${choice}" ${value === choice ? 'selected' : ''}>${choice}</option>`).join('');
      } else {
        const range = this.ranges[definition.range];
        const values = rangeValues(range);
        const format = definition.format || ((number) => (definition.signed ? formatSigned(number, range.decimals) : number.toFixed(range.decimals)));
        const option = (number) => {
          const text = format(number);
          return `<option value="${text}" ${value === text ? 'selected' : ''}>${text}</option>`;
        };

        if (definition.signed && range.min < 0 && range.max > 0) {
          const minus = values.filter((number) => number < 0).reverse();
          const plus = values.filter((number) => number > 0);
          options += values.includes(0) ? option(0) : '';
          options += `<optgroup label="Minus (&minus;)">${minus.map(option).join('')}</optgroup>`;
          options += `<optgroup label="Plus (+)">${plus.map(option).join('')}</optgroup>`;
        } else {
          options += values.map(option).join('');
        }
      }
      return `<select class="rx-select" name="${name}" ${attributes}>${options}</select>`;
    }

    _rxGrid(title, fields, { optional } = {}) {
      return `
        <fieldset class="rx-grid" style="--rx-columns: ${fields.length}">
          <legend class="rx-grid__legend">${title}${optional ? ' <span class="rx-optional">(optional)</span>' : ''}</legend>
          <span></span>
          ${fields.map((field) => `<span class="rx-grid__heading">${RX_FIELDS[field].label}</span>`).join('')}
          ${EYES.map((eye) => `
            <span class="rx-grid__eye">${eye.label}<small>${eye.title}</small></span>
            ${fields.map((field) => this._select(`rx.${eye.key}.${field}`, field, this.state.rx[eye.key][field], `aria-label="${eye.label} ${RX_FIELDS[field].label}"`)).join('')}
          `).join('')}
        </fieldset>
      `;
    }

    _render_rx() {
      const { state } = this;
      const isProgressive = state.type === 'Progressive';
      const pdRangeSingle = this.ranges.pd_single;
      const pdRangeDual = this.ranges.pd_dual;
      const pdSelect = (name, range, value, label) => {
        const options = rangeValues(range).map((number) => {
          const text = number.toFixed(range.decimals);
          return `<option value="${text}" ${value === text ? 'selected' : ''}>${text}</option>`;
        }).join('');
        return `<select class="rx-select" name="${name}" aria-label="${label}"><option value="">&ndash;</option>${options}</select>`;
      };

      return `
        <h2 class="rx-step-title">Enter Prescription</h2>

        <label class="rx-field">
          <span class="rx-field__label">Patient name / reference</span>
          <input class="rx-input" type="text" name="patient" value="${escapeHtml(state.patient)}" maxlength="100" autocomplete="off" required>
        </label>

        ${this._rxGrid('Prescription', isProgressive ? ['sph', 'cyl', 'axis', 'add'] : ['sph', 'cyl', 'axis'])}

        <fieldset class="rx-pd">
          <legend class="rx-grid__legend">Pupillary Distance (PD)</legend>
          <div class="rx-segmented" role="radiogroup">
            <label><input type="radio" name="pdMode" value="single" ${state.pdMode === 'single' ? 'checked' : ''}> Single PD</label>
            <label><input type="radio" name="pdMode" value="dual" ${state.pdMode === 'dual' ? 'checked' : ''}> Dual PD</label>
          </div>
          ${state.pdMode === 'single'
            ? `<div class="rx-pd__row">${pdSelect('pd.single', pdRangeSingle, state.pd.single, 'PD')}<span>mm</span></div>`
            : `<div class="rx-pd__row">
                <label>OD ${pdSelect('pd.od', pdRangeDual, state.pd.od, 'Right PD')}</label>
                <label>OS ${pdSelect('pd.os', pdRangeDual, state.pd.os, 'Left PD')}</label>
                <span>mm</span>
              </div>`}
        </fieldset>

        ${this._rxGrid('Prism', ['prism_h', 'prism_h_base', 'prism_v', 'prism_v_base'], { optional: true })}
        ${this._rxGrid('Measurements (mm / D)', ['base_curve', 'seg_height', 'oc_height'], { optional: !isProgressive })}
        ${this._rxGrid('Thickness (mm)', ['center_thickness', 'edge_thickness'], { optional: true })}

        <label class="rx-field">
          <span class="rx-field__label">Special instructions <span class="rx-optional">(optional)</span></span>
          <textarea class="rx-input" name="notes" rows="3" maxlength="1000">${escapeHtml(state.notes)}</textarea>
        </label>
      `;
    }

    _render_tint() {
      const groups = Object.keys(LENS_STYLES).map((technology) => ({
        technology,
        tints: this.config.tints.filter((tint) => tint.technology === technology && tint.variantId)
      })).filter((group) => group.tints.length);

      return `
        <h2 class="rx-step-title">SpectraGuard Technology &amp; Tint</h2>
        ${groups.map((group) => `
          <h3 class="rx-group-title">SpectraGuard ${escapeHtml(group.technology)}</h3>
          <div class="rx-options">
            ${group.tints.map((tint) => this._optionCard({
              name: 'tint',
              value: tint.handle,
              selected: this.state.tint === tint.handle,
              title: tint.name,
              description: `SpectraGuard ${group.technology}`,
              price: this._money(tint.price),
              swatch: tint.color,
              disabled: tint.available === false
            })).join('')}
          </div>
        `).join('')}
      `;
    }

    _render_style() {
      return `
        <h2 class="rx-step-title">Lens Style</h2>
        <div class="rx-options">
          ${this._stylesForTechnology().map((style) => this._optionCard({
            name: 'style', value: style.key, selected: this.state.style === style.key, title: style.label
          })).join('')}
        </div>
      `;
    }

    _render_design() {
      const designs = this._availableDesigns();
      if (!designs.length) {
        return `<h2 class="rx-step-title">Lens Design</h2><p class="rx-empty">No lens designs are available for this combination.</p>`;
      }
      return `
        <h2 class="rx-step-title">Lens Design</h2>
        <div class="rx-options">
          ${designs.map((design) => this._optionCard({
            name: 'design',
            value: design.handle,
            selected: this.state.design === design.handle,
            title: design.name,
            description: design.summary,
            price: `From ${this._money(this._lowestPrice(design.products[this.state.style]))}`,
            info: Boolean(design.details || design.images.length)
          })).join('')}
        </div>
      `;
    }

    _render_index() {
      const product = this._selectedLensProduct();
      const design = this._selectedDesign();
      const lowest = this._lowestPrice(product);
      return `
        <h2 class="rx-step-title">Select Index for ${escapeHtml(design.name)}</h2>
        <div class="rx-options">
          ${product.variants.map((variant) => this._optionCard({
            name: 'index',
            value: variant.id,
            selected: this.state.variantId === variant.id,
            title: variant.title,
            price: variant.price === lowest ? 'Included' : `+${this._money(variant.price - lowest)}`,
            disabled: !variant.available
          })).join('')}
        </div>
      `;
    }

    _render_addons(group) {
      return `
        <h2 class="rx-step-title">${escapeHtml(ADD_ON_GROUPS.find((item) => item.key === group).title)}</h2>
        <div class="rx-options">
          ${this._addOnsInGroup(group).map((addOn) => `
            <label class="rx-option rx-option--check${this.state.addOns.has(addOn.handle) ? ' is-selected' : ''}">
              <input type="checkbox" name="addon" value="${escapeHtml(addOn.handle)}" ${this.state.addOns.has(addOn.handle) ? 'checked' : ''} ${addOn.available === false ? 'disabled' : ''}>
              <span class="rx-option__text"><span class="rx-option__title">${escapeHtml(addOn.title)}</span></span>
              <span class="rx-option__price">${addOn.price ? this._money(addOn.price) : 'Included'}</span>
            </label>
          `).join('')}
        </div>
      `;
    }

    _render_review() {
      const summary = this._readableProperties();
      const tint = this._selectedTint();
      const variant = this._selectedVariant();
      const design = this._selectedDesign();
      const addOns = this._selectedAddOns();

      return `
        <h2 class="rx-step-title">Please Review Your Order</h2>
        <dl class="rx-review">
          ${Object.entries(summary).map(([label, value]) => `<div><dt>${escapeHtml(label)}</dt><dd>${escapeHtml(value)}</dd></div>`).join('')}
        </dl>
        <h3 class="rx-group-title">Charges</h3>
        <dl class="rx-review rx-review--charges">
          <div><dt>${escapeHtml(design.name)} &middot; ${escapeHtml(variant.title)}</dt><dd>${this._money(variant.price)}</dd></div>
          <div><dt>SpectraGuard ${escapeHtml(tint.technology)} &middot; ${escapeHtml(tint.name)}</dt><dd>${this._money(tint.price)}</dd></div>
          ${addOns.map((addOn) => `<div><dt>${escapeHtml(addOn.title)}</dt><dd>${addOn.price ? this._money(addOn.price) : 'Included'}</dd></div>`).join('')}
        </dl>
      `;
    }

    // -------------------------------------------------------------------------------------------------------------------
    // Interaction
    // -------------------------------------------------------------------------------------------------------------------

    _onBodyClick(event) {
      const infoButton = event.target.closest('[data-rx-info-open]');
      if (infoButton) {
        this._openInfo(infoButton.dataset.rxInfoOpen);
        return;
      }

      const option = event.target.closest('.rx-option__select');
      if (!option || option.disabled) {
        return;
      }
      const { state } = this;
      const [[key, value]] = Object.entries(option.dataset);

      switch (key) {
        case 'rxType':
          if (state.type !== value) {
            state.type = value;
            state.design = null;
            state.variantId = null;
          }
          break;
        case 'rxTint': {
          const tint = this.config.tints.find((item) => item.handle === value);
          if (state.technology !== tint.technology) {
            state.style = null;
            state.design = null;
            state.variantId = null;
          }
          state.tint = value;
          state.technology = tint.technology;

          // The style step is skipped when a technology has a single style (Clear for Sun), so select it here.
          const styles = this._stylesForTechnology();
          if (styles.length === 1) {
            state.style = styles[0].key;
          }
          break;
        }
        case 'rxStyle':
          if (state.style !== value) {
            state.style = value;
            state.variantId = null;
            if (state.design && !this._availableDesigns().some((design) => design.handle === state.design)) {
              state.design = null;
            }
          }
          break;
        case 'rxDesign': {
          state.design = value;
          const product = this._selectedLensProduct();
          if (!product.variants.some((variant) => variant.id === state.variantId)) {
            // Default to the lowest-priced available index; the index step lets the customer change it.
            const available = product.variants.filter((variant) => variant.available);
            state.variantId = (available.length === 1 || product.variants.length === 1) ? (available[0] || product.variants[0]).id : null;
          }
          break;
        }
        case 'rxIndex':
          state.variantId = Number(value);
          break;
        default:
          return;
      }
      this.next();
    }

    _onBodyChange(event) {
      const { target } = event;
      const { state } = this;

      if (target.name === 'patient' || target.name === 'notes') {
        state[target.name] = target.value;
      } else if (target.name === 'pdMode') {
        state.pdMode = target.value;
        this.render();
      } else if (target.name?.startsWith('pd.')) {
        state.pd[target.name.slice(3)] = target.value;
      } else if (target.name?.startsWith('rx.')) {
        const [, eye, field] = target.name.split('.');
        state.rx[eye][field] = target.value;
      } else if (target.name === 'addon') {
        target.checked ? state.addOns.add(target.value) : state.addOns.delete(target.value);
        target.closest('.rx-option').classList.toggle('is-selected', target.checked);
        this.dialog.querySelector('[data-rx-next]').textContent = this._addOnsInGroup(this.step.split(':')[1])
          .some((addOn) => state.addOns.has(addOn.handle)) ? 'Continue' : 'Skip';
        this._renderSummary();
      }
    }

    _openInfo(handle) {
      const design = this.config.designs.find((item) => item.handle === handle);
      const overlay = this.dialog.querySelector('[data-rx-info]');
      overlay.querySelector('[data-rx-info-content]').innerHTML = `
        <h3 class="rx-step-title">${escapeHtml(design.name)}</h3>
        ${design.summary ? `<p class="rx-info__summary">${escapeHtml(design.summary)}</p>` : ''}
        ${design.images.map((image) => `<img src="${escapeHtml(image.src)}" alt="${escapeHtml(image.alt)}" loading="lazy">`).join('')}
        <div class="rx-info__details">${design.details}</div>
      `;
      overlay.hidden = false;
      overlay.querySelector('[data-rx-info-close]').focus();
    }

    _closeInfo() {
      this.dialog.querySelector('[data-rx-info]').hidden = true;
    }

    _showError(message) {
      const error = this.dialog.querySelector('[data-rx-error]');
      error.textContent = message || '';
      error.hidden = !message;
    }

    // -------------------------------------------------------------------------------------------------------------------
    // Cart
    // -------------------------------------------------------------------------------------------------------------------

    _eyeSummary(eye) {
      const values = this.state.rx[eye];
      const parts = [['SPH', values.sph], ['CYL', values.cyl], ['AXIS', values.axis], ['ADD', values.add]]
        .filter(([, value]) => value)
        .map(([label, value]) => `${label} ${value}`);
      return parts.join(' ');
    }

    _readableProperties() {
      const { state } = this;
      const tint = this._selectedTint();
      const design = this._selectedDesign();
      const variant = this._selectedVariant();
      const style = this._stylesForTechnology().find((item) => item.key === state.style);
      const addOns = this._selectedAddOns();
      const properties = {
        Patient: state.patient.trim(),
        'Prescription Type': state.type,
        'OD (Right)': this._eyeSummary('od'),
        'OS (Left)': this._eyeSummary('os'),
        PD: state.pdMode === 'single' ? `${state.pd.single} mm` : `OD ${state.pd.od} / OS ${state.pd.os} mm`
      };

      EYES.forEach((eye) => {
        const values = state.rx[eye.key];
        const prism = ['h', 'v']
          .filter((axis) => values[`prism_${axis}`])
          .map((axis) => `${values[`prism_${axis}`]} BASE ${values[`prism_${axis}_base`]}`);
        if (prism.length) {
          properties[`Prism ${eye.label}`] = prism.join(', ');
        }
        const measurements = [['BC', 'base_curve'], ['Seg', 'seg_height'], ['OC', 'oc_height'], ['CT', 'center_thickness'], ['ET', 'edge_thickness']]
          .filter(([, field]) => values[field])
          .map(([label, field]) => `${label} ${values[field]}`);
        if (measurements.length) {
          properties[`Measurements ${eye.label}`] = measurements.join(' · ');
        }
      });

      properties.SpectraGuard = `${tint.technology} · ${tint.name}`;
      properties.Lens = `${design.name} · ${variant.title} · ${style ? style.label : ''}`;
      ADD_ON_GROUPS.forEach((group) => {
        const titles = addOns.filter((addOn) => addOn.group === group.key).map((addOn) => addOn.title);
        if (titles.length) {
          properties[group.title] = titles.join(', ');
        }
      });
      if (state.notes.trim()) {
        properties['Special Instructions'] = state.notes.trim();
      }
      return properties;
    }

    _rxPayload() {
      const { state } = this;
      const tint = this._selectedTint();
      const design = this._selectedDesign();
      const variant = this._selectedVariant();
      const style = this._stylesForTechnology().find((item) => item.key === state.style);
      const clean = (values) => Object.fromEntries(Object.entries(values).filter(([, value]) => value !== '' && value !== undefined));

      return {
        v: 1,
        patient: state.patient.trim(),
        type: state.type,
        od: clean(state.rx.od),
        os: clean(state.rx.os),
        pd: state.pdMode === 'single' ? { single: state.pd.single } : { od: state.pd.od, os: state.pd.os },
        technology: tint.technology,
        tint: tint.name,
        style: style ? style.label : state.style,
        design: design.name,
        index: variant.title,
        addOns: this._selectedAddOns().map((addOn) => ({ group: addOn.group, title: addOn.title })),
        notes: state.notes.trim()
      };
    }

    async addToCart() {
      const nextButton = this.dialog.querySelector('[data-rx-next]');
      const group = uuid();
      const patient = this.state.patient.trim();
      const tint = this._selectedTint();
      const style = this._stylesForTechnology().find((item) => item.key === this.state.style);
      const childProperties = (role, extra = {}) => ({ ...extra, Patient: patient, _rx_group: group, _rx_role: role });

      // The cart lists the lines of one add request in reverse, so the frame goes last to be shown first.
      const items = [
        ...this._selectedAddOns().reverse().map((addOn) => ({ id: addOn.variantId, quantity: 1, properties: childProperties('addon') })),
        { id: tint.variantId, quantity: 1, properties: childProperties('addon', { Tint: tint.name }) },
        { id: this._selectedVariant().id, quantity: 1, properties: childProperties('lens', style ? { 'Lens Style': style.label } : {}) },
        {
          id: this.config.frame.variantId,
          quantity: 1,
          properties: {
            ...this._readableProperties(),
            _rx: JSON.stringify(this._rxPayload()),
            _rx_group: group,
            _rx_role: 'frame'
          }
        }
      ];

      nextButton.disabled = true;
      nextButton.setAttribute('aria-busy', 'true');
      try {
        const response = await fetch(`${this.config.rootUrl.replace(/\/$/, '')}/cart/add.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items })
        });
        const json = await response.json();
        if (!response.ok) {
          throw new Error(json.description || json.message || 'Could not add to cart.');
        }
      } catch (error) {
        this._showError(error.message);
        return;
      } finally {
        nextButton.disabled = false;
        nextButton.removeAttribute('aria-busy');
      }

      this.dialog.close();
      if (this.config.cartType === 'page') {
        window.location.href = this.config.cartUrl;
        return;
      }
      document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
      document.querySelector('cart-drawer')?.show?.();
    }
  }

  window.customElements.define('rx-lens-selector', RxLensSelector);
})();
